import React, { useState } from "react";
import { ArrowLeft, ChevronDown, ChevronUp } from "lucide-react";
import { getStatusFilterOptions } from "./CardsView.utils";
import {
  DashboardControls as StyledDashboardControls,
  ControlLabel,
  ControlSelect,
  ControlInput,
  RefreshButton,
  BackButton,
  CardsPerPageContainer,
  CardsPerPageLabel,
  CardsPerPageSelect,
} from "./CardsView.styles";

const DashboardControls = ({
  ordering,
  search,
  cardsPerPage,
  columnFilters,
  batchViewMode,
  onOrderingChange,
  onSearchSubmit,
  onSearchChange,
  onRefresh,
  onBack,
  onClearFilters,
  onStatusFilterChange,
  onOcrStatusFilterChange,
  onCardsPerPageChange,
  onPersonalizeModalOpen,
  onBulkEditModalOpen,
  onBulkReadyToList,
  onBulkMarkAsDraft,
  onBulkDeleteModalOpen,
  onAddCardModalOpen,
  onViewModeChange,
  onListOnEbay,
  onTitleBuilderModalOpen,
  isListingOnEbay = false,
  selectedCardsCount = 0,
  totalCardsCount = 0,
  readyCardsCount = 0,
  listOnEbayCount,
  showListOnEbay = false,
  notReadySelectedCardsCount = 0,
  readySelectedCardsCount = 0,
  isAllPagesSelected = false,
  showBackButton = false,
  showCardsPerPage = true,
  isSameAsGlobal = null,
  isEditModeEnabled = true,
  onEditModeChange,
  showTitleLengthFilter = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const statusFilterOptions = getStatusFilterOptions({ showTitleLengthFilter });
  const hasActiveFilters =
    ordering !== "uploaded_at" ||
    Boolean(search && search.trim()) ||
    Number(cardsPerPage) !== 100 ||
    Boolean(columnFilters.status) ||
    Boolean(columnFilters.ocr_status);
  const rowStyle = {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    flexWrap: "wrap",
    width: "100%",
  };

  return (
    <StyledDashboardControls
      style={{
        flexDirection: "column",
        alignItems: "stretch",
        gap: isExpanded ? "0.5rem" : 0,
      }}
    >
      {/* Row 1: Filters */}
      <div style={rowStyle}>
      {showBackButton && (
        <BackButton onClick={onBack} onFocus={(e) => e.target.blur()}>
          <ArrowLeft size={14} />
          Back
        </BackButton>
      )}

        <div style={{ display: "flex", alignItems: "center", gap: "0.375rem" }}>
          <ControlLabel htmlFor="ordering">Sort:</ControlLabel>
          <ControlSelect
            id="ordering"
            value={ordering}
            onChange={onOrderingChange}
            $active={ordering !== "uploaded_at"}
          >
            <option value="-uploaded_at">Newest First</option>
            <option value="uploaded_at">Oldest First</option>
            <option value="title">Title A-Z</option>
            <option value="-title">Title Z-A</option>
            <option value="status">Status</option>
            <option value="-status">Status (Reverse)</option>
            <option value="ebay_card_number">Card Number</option>
          </ControlSelect>
        </div>

        <form
          onSubmit={onSearchSubmit}
          style={{ display: "flex", alignItems: "center", gap: "0.375rem" }}
        >
          <ControlLabel htmlFor="search">Search:</ControlLabel>
          <ControlInput
            id="search"
            type="text"
            placeholder="Search cards..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            $active={Boolean(search && search.trim())}
          />
        </form>

        {showCardsPerPage && batchViewMode !== "inspector" && (
          <CardsPerPageContainer>
            <CardsPerPageLabel htmlFor="cardsPerPage">Per page:</CardsPerPageLabel>
            <CardsPerPageSelect
              id="cardsPerPage"
              value={cardsPerPage}
              onChange={onCardsPerPageChange}
              $active={Number(cardsPerPage) !== 100}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </CardsPerPageSelect>
          </CardsPerPageContainer>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: "0.375rem" }}>
          <ControlLabel htmlFor="statusFilter">Status:</ControlLabel>
          <ControlSelect
            id="statusFilter"
            value={columnFilters.status || ""}
            onChange={(e) => onStatusFilterChange(e.target.value)}
            $active={Boolean(columnFilters.status)}
          >
            {statusFilterOptions.map((option) => (
              <option key={option.value || "all"} value={option.value}>
                {option.label}
              </option>
            ))}
          </ControlSelect>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "0.375rem" }}>
          <ControlLabel htmlFor="ocrFilter">OCR:</ControlLabel>
          <ControlSelect
            id="ocrFilter"
            value={columnFilters.ocr_status || ""}
            onChange={(e) => onOcrStatusFilterChange(e.target.value)}
            $active={Boolean(columnFilters.ocr_status)}
          >
            <option value="">All</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </ControlSelect>
        </div>

        <RefreshButton
          onClick={onClearFilters}
          onFocus={(e) => e.target.blur()}
          $primary={hasActiveFilters}
        >
          Clear Filters
        </RefreshButton>

        <RefreshButton
          onClick={() => setIsExpanded((prev) => !prev)}
          onFocus={(e) => e.target.blur()}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.25rem",
            marginLeft: "auto",
          }}
        >
          More options
          {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </RefreshButton>
      </div>

      {/* Row 2: Actions */}
      {isExpanded && (
      <div
        style={{
          ...rowStyle,
          paddingTop: "0.5rem",
          borderTop: "1px solid #e5e7eb",
        }}
      >
        {batchViewMode !== "inspector" && (
          <RefreshButton
            onClick={onPersonalizeModalOpen}
            onFocus={(e) => e.target.blur()}
          >
            Personalize
          </RefreshButton>
        )}

        {(selectedCardsCount > 0 || isAllPagesSelected) && (
          <>
            {notReadySelectedCardsCount > 0 && (
              <RefreshButton
                onClick={onBulkReadyToList}
                onFocus={(e) => e.target.blur()}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#f0fdf4";
                  e.currentTarget.style.borderColor = "#10b981";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "white";
                  e.currentTarget.style.borderColor = "#e5e7eb";
                }}
                style={{
                  color: "#10b981",
                  outline: "none",
                }}
              >
                Ready ({notReadySelectedCardsCount})
              </RefreshButton>
            )}
            {readySelectedCardsCount > 0 && (
              <RefreshButton
                onClick={onBulkMarkAsDraft}
                onFocus={(e) => e.target.blur()}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#fffbeb";
                  e.currentTarget.style.borderColor = "#f59e0b";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "white";
                  e.currentTarget.style.borderColor = "#e5e7eb";
                }}
                style={{
                  color: "#f59e0b",
                  outline: "none",
                }}
              >
                Draft ({readySelectedCardsCount})
              </RefreshButton>
            )}
            <RefreshButton
              onClick={onBulkDeleteModalOpen}
              onFocus={(e) => e.target.blur()}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#fef2f2";
                e.currentTarget.style.borderColor = "#ef4444";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "white";
                e.currentTarget.style.borderColor = "#e5e7eb";
              }}
              style={{
                color: "#ef4444",
                outline: "none",
              }}
            >
              Delete ({selectedCardsCount})
            </RefreshButton>
          </>
        )}

        <RefreshButton
          onClick={onAddCardModalOpen}
          onFocus={(e) => e.target.blur()}
        >
          Add Card
        </RefreshButton>

        <RefreshButton
          onClick={onTitleBuilderModalOpen}
          onFocus={(e) => e.target.blur()}
        >
          Default Title Format
        </RefreshButton>

        {(showListOnEbay || readyCardsCount > 0 || (listOnEbayCount ?? 0) > 0) && (
          <RefreshButton
            onClick={onListOnEbay}
            onFocus={(e) => e.target.blur()}
            disabled={isListingOnEbay}
          >
            {isListingOnEbay
              ? "Syncing..."
              : `List/Resync (${listOnEbayCount ?? readyCardsCount})`}
          </RefreshButton>
        )}

        <RefreshButton onClick={onRefresh} onFocus={(e) => e.target.blur()}>
          Refresh
        </RefreshButton>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            marginLeft: "auto",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0.375rem 0.625rem",
              background: "white",
              borderRadius: "8px",
              border: "1px solid #e5e7eb",
              boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
            }}
          >
            <span
              style={{
                fontSize: "0.8125rem",
                fontWeight: "500",
                color: "#374151",
              }}
            >
              Edit:
            </span>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              <div
                style={{
                  position: "relative",
                  width: "44px",
                  height: "24px",
                  borderRadius: "12px",
                  background: isEditModeEnabled ? "#3b82f6" : "#d1d5db",
                  transition: "background-color 0.2s ease",
                  outline: "none",
                }}
              >
                <input
                  type="checkbox"
                  checked={isEditModeEnabled}
                  onChange={(e) => onEditModeChange?.(e.target.checked)}
                  style={{
                    position: "absolute",
                    opacity: 0,
                    width: 0,
                    height: 0,
                  }}
                  onFocus={(e) => e.target.blur()}
                />
                <div
                  style={{
                    position: "absolute",
                    top: "2px",
                    left: isEditModeEnabled ? "22px" : "2px",
                    width: "20px",
                    height: "20px",
                    borderRadius: "50%",
                    background: "white",
                    transition: "left 0.2s ease",
                    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.2)",
                  }}
                />
              </div>
              <span
                style={{
                  fontSize: "0.8125rem",
                  fontWeight: "500",
                  color: "#374151",
                }}
              >
                {isEditModeEnabled ? "On" : "Off"}
              </span>
            </label>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "0.375rem" }}>
            <ControlLabel>View:</ControlLabel>
            <div
              style={{
                display: "flex",
                background: "#f3f4f6",
                borderRadius: "6px",
                padding: "2px",
              }}
            >
              <button
                onClick={() => onViewModeChange("overview")}
                style={{
                  padding: "0.375rem 0.75rem",
                  border: "none",
                  borderRadius: "4px",
                  fontSize: "0.8125rem",
                  fontWeight: "500",
                  cursor: "pointer",
                  transition: "background-color 0.2s ease, color 0.2s ease",
                  background:
                    batchViewMode === "overview" ? "#ffffff" : "transparent",
                  color: batchViewMode === "overview" ? "#1f2937" : "#6b7280",
                  outline: "none",
                }}
                onFocus={(e) => e.target.blur()}
              >
                Table
              </button>
              <button
                onClick={() => onViewModeChange("inspector")}
                style={{
                  padding: "0.375rem 0.75rem",
                  border: "none",
                  borderRadius: "4px",
                  fontSize: "0.8125rem",
                  fontWeight: "500",
                  cursor: "pointer",
                  transition: "background-color 0.2s ease, color 0.2s ease",
                  background:
                    batchViewMode === "inspector" ? "#ffffff" : "transparent",
                  color: batchViewMode === "inspector" ? "#1f2937" : "#6b7280",
                  outline: "none",
                }}
                onFocus={(e) => e.target.blur()}
              >
                Inspector
              </button>
            </div>
          </div>
        </div>
      </div>
      )}
    </StyledDashboardControls>
  );
};

export default DashboardControls;
